// frontend/src/lib/workflowState.js

const HANDOFF_PREFIX = 'aria_action_';
const DRAFT_PREFIX = 'aria_draft_';
const CONV_PREFIX = 'aria_conv_';
const HANDOFF_TTL_MS = 30 * 60 * 1000; // 30 minutes
const CONV_MAX_MESSAGES = 200;

// ── UUID ────────────────────────────────────────────────────────────────────
function generateId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID().replace(/-/g, '');
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// ── Handoff (cross-tab, short-lived) ────────────────────────────────────────

/**
 * Write a pre-fill payload for cross-tab handoff.
 * Returns the localStorage key to pass as ?action_key=<key> in the URL.
 */
export function writeHandoff(payload) {
  const key = HANDOFF_PREFIX + generateId();
  localStorage.setItem(key, JSON.stringify({ payload, ts: Date.now() }));
  return key;
}

/**
 * Read and immediately delete a handoff by key.
 * Returns the payload object, or null if the key doesn't exist or TTL expired.
 */
export function consumeHandoff(key) {
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  localStorage.removeItem(key);
  try {
    const { payload, ts } = JSON.parse(raw);
    if (Date.now() - ts > HANDOFF_TTL_MS) return null;
    return payload;
  } catch {
    return null;
  }
}

/**
 * Delete all handoff keys older than HANDOFF_TTL_MS.
 * Call once on app boot.
 */
export function sweepStaleHandoffs() {
  const now = Date.now();
  Object.keys(localStorage)
    .filter(k => k.startsWith(HANDOFF_PREFIX))
    .forEach(k => {
      try {
        const { ts } = JSON.parse(localStorage.getItem(k));
        if (now - ts > HANDOFF_TTL_MS) localStorage.removeItem(k);
      } catch {
        localStorage.removeItem(k); // corrupt entry — remove it
      }
    });
}

// ── Draft (named, persists until submit/cancel) ──────────────────────────────

/**
 * Write the original pre-fill payload as a named draft for this form.
 * module: e.g. 'factory', form: e.g. 'create_wo'
 */
export function writeDraft(module, form, payload) {
  const key = `${DRAFT_PREFIX}${module}_${form}`;
  localStorage.setItem(key, JSON.stringify(payload));
}

/** Read draft payload. Returns object or null. */
export function readDraft(module, form) {
  const raw = localStorage.getItem(`${DRAFT_PREFIX}${module}_${form}`);
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

/** Delete draft — call on form submit success or explicit cancel. */
export function clearDraft(module, form) {
  localStorage.removeItem(`${DRAFT_PREFIX}${module}_${form}`);
}

// ── AI Conversation history ──────────────────────────────────────────────────

export function readConversation(userId) {
  const raw = localStorage.getItem(`${CONV_PREFIX}${userId}`);
  if (!raw) return [];
  try { return JSON.parse(raw); } catch { return []; }
}

export function writeConversation(userId, messages) {
  const capped = messages.slice(-CONV_MAX_MESSAGES);
  try {
    localStorage.setItem(`${CONV_PREFIX}${userId}`, JSON.stringify(capped));
  } catch {
    // localStorage quota exceeded — silently drop (history is non-critical)
  }
}

export function clearConversation(userId) {
  localStorage.removeItem(`${CONV_PREFIX}${userId}`);
}
